import isEmpty from 'lodash/isEmpty';
import {
    CROSS_MODULE_TASK_STEPS,
    TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW,
    TAB_TO_TASK_TRACKER_MAP,
    TabModule,
} from '../../utils/constants/commonConstants';
import { TAB_LOCK_COMPLETED_STATUSES } from '../../modules/Dashboard/DashboardModules/RelatedParties/relatedParty_constants';

const SECONDARY_TAB_CATEGORY = 'secondary';

type TabWithCategory = { id: string; category?: string };

/** Ids of the dashboard config's secondary-category tabs; undefined when the config has no tabs yet. */
export function getSecondaryTabIds(tabs: TabWithCategory[]): string[];
export function getSecondaryTabIds(tabs?: TabWithCategory[]): string[] | undefined;
export function getSecondaryTabIds(tabs?: TabWithCategory[]) {
    return tabs?.filter((tab) => tab.category === SECONDARY_TAB_CATEGORY).map((tab) => tab.id);
}

export type TaskTrackerMap = { [taskTrackerStep: string]: { status: string; dateCreated: number } };

export type ModuleLastRefreshedAt = { [moduleId: string]: number };

export type RefreshNudge =
    | {
          orgId: string;
          status: 'loaded' | 'refreshing';
          showNudge: boolean;
          lastRefreshedAt: number | null;
          moduleLastRefreshedAt: ModuleLastRefreshedAt;
      }
    | { orgId: string; status: 'error' };

export type DashboardTab = { id: string; category?: string; refreshableModule?: boolean; [key: string]: any };

/** Per-step grouped progress on the tracker read; absent for orgs without per-person rows. */
export type StepProgress = Record<string, { inProgress: boolean; completed?: number }>;

export function getRefreshableTabs(tabs: DashboardTab[] | undefined): DashboardTab[] {
    return (tabs ?? []).filter((tab) => tab.refreshableModule === true);
}

/**
 * Merges a fresh task tracker fetch into the sticky CROSS_MODULE_TASK_STEPS cache: once a shared
 * step (e.g. AION_INGEST_BASE_DETAILS) reaches a terminal status, a later non-terminal read of
 * that same step (e.g. Overview's refresh soft-resetting it) must not un-terminal it here — see
 * CROSS_MODULE_TASK_STEPS's own doc comment in commonConstants.
 */
export function mergeCrossModuleCacheEntries(previousCache: TaskTrackerMap, taskTrackerMap: TaskTrackerMap): TaskTrackerMap {
    const merged: TaskTrackerMap = { ...previousCache };
    for (const [step, entry] of Object.entries(taskTrackerMap)) {
        if (
            !CROSS_MODULE_TASK_STEPS.has(step) ||
            TAB_LOCK_COMPLETED_STATUSES.has(entry.status) ||
            !TAB_LOCK_COMPLETED_STATUSES.has(previousCache[step]?.status ?? '')
        ) {
            merged[step] = entry;
        }
    }
    return merged;
}

/**
 * Resolves a single step's effective status against a live taskTrackerMap and the sticky
 * cross-module cache. Two callers need this, and they disagree on how much the cache is allowed
 * to override a live read:
 *
 * - `crossModuleOnly: true` (isManagementProfileLocked) — the cache only wins over a live status
 *   for a CROSS_MODULE_TASK_STEPS member once terminal; for every other step, a live taskTrackerMap
 *   always wins, and the cache is consulted only when the WHOLE taskTrackerMap hasn't landed yet.
 * - `crossModuleOnly: false` (isManagementProfileFetchInFlight) — any terminal cached status wins
 *   over a live one, for any step, regardless of CROSS_MODULE_TASK_STEPS membership.
 *
 * Each caller's existing semantics are preserved exactly — see their own doc comments for why.
 */
function resolveEffectiveStepStatus(
    step: string,
    taskTrackerMap: TaskTrackerMap,
    cache: TaskTrackerMap,
    options: { crossModuleOnly: boolean },
): string | undefined {
    const cachedStatus = cache[step]?.status;

    if (!options.crossModuleOnly) {
        return TAB_LOCK_COMPLETED_STATUSES.has(cachedStatus ?? '') ? cachedStatus : taskTrackerMap?.[step]?.status ?? cachedStatus;
    }

    if (CROSS_MODULE_TASK_STEPS.has(step) && TAB_LOCK_COMPLETED_STATUSES.has(cachedStatus ?? '')) return cachedStatus;
    const effectiveTaskTrackerMap = isEmpty(taskTrackerMap) ? cache : taskTrackerMap;
    return step in effectiveTaskTrackerMap ? effectiveTaskTrackerMap[step].status : cachedStatus;
}

/**
 * Single source of truth for whether the Management Profile tab is locked — shared by
 * TabSelector's sidebar and RefreshReport's drawer so the two surfaces can't read this tab's
 * lock state differently again (an org with 0 TaskTracker rows used to read locked in the
 * sidebar and unlocked in the drawer).
 *
 * `resolved` must be false until the org's task tracker fetch has actually returned — an empty
 * taskTrackerMap is genuinely ambiguous between "not fetched yet" and "fetched, this org has
 * none", and only the caller's own resolved signal can tell those two apart.
 */
export function isManagementProfileLocked(
    resolved: boolean,
    taskTrackerMap: TaskTrackerMap,
    cache: TaskTrackerMap,
    // Typed loosely: InvestorReducer declares this field as a single { isLocked } object, but
    // every caller (this included) actually indexes it by tabId — matches the existing usage.
    tabLockStatus: any,
): boolean {
    if (!resolved) return false;

    const steps = TAB_TO_TASK_TRACKER_MAP[TabModule.MANAGEMENT_PROFILE] || [];

    let anyStepCompleteOrFailed = false;
    if (!isEmpty(taskTrackerMap) || !isEmpty(cache)) {
        const stepStatuses = steps
            .map((step) => resolveEffectiveStepStatus(step, taskTrackerMap, cache, { crossModuleOnly: true }))
            .filter((status): status is string => status !== undefined);
        anyStepCompleteOrFailed = !isEmpty(stepStatuses) && stepStatuses.some((status) => TAB_LOCK_COMPLETED_STATUSES.has(status));
    }

    return !!(!anyStepCompleteOrFailed || tabLockStatus?.[TabModule.MANAGEMENT_PROFILE]?.isLocked);
}

/**
 * True while any Management Profile step (the same TAB_TO_TASK_TRACKER_MAP entry
 * isManagementProfileLocked reads above — the bootstrap base-details ingest and the DIN fetch)
 * is present but not yet terminal (COMPLETE/FAILED/FAILURE). Absent is never in flight, so an org
 * that never ran a step doesn't spin forever. Shared by TabSelector's sidebar spinner and
 * RefreshReport's drawer Status cell so the two surfaces can't disagree, the same way they
 * already share isManagementProfileLocked.
 */
export function isManagementProfileFetchInFlight(taskTrackerMap: TaskTrackerMap, cache: TaskTrackerMap): boolean {
    const steps = TAB_TO_TASK_TRACKER_MAP[TabModule.MANAGEMENT_PROFILE] || [];
    return steps.some((step) => {
        const status = resolveEffectiveStepStatus(step, taskTrackerMap, cache, { crossModuleOnly: false });
        return !!status && !TAB_LOCK_COMPLETED_STATUSES.has(status);
    });
}

function latestCompleteDate(steps: string[], taskTrackerMap: TaskTrackerMap): number | undefined {
    return steps
        .filter((task) => taskTrackerMap[task]?.status === 'COMPLETE')
        .map((task) => taskTrackerMap[task]?.dateCreated)
        .filter(Boolean)
        .sort((a, b) => b - a)[0];
}

export const BE_REFRESHABLE_MODULES: string[] = [
    TabModule.OVERVIEW,
    TabModule.MANAGEMENT_PROFILE,
    TabModule.LEGAL_CHECKS,
    TabModule.MCA_FILING,
];

// Prefers module-exclusive steps so another module's refresh can't move this module's date.
export function getModuleLastFetched(
    moduleId: string,
    taskTrackerMap: TaskTrackerMap,
    moduleLastRefreshedAt?: ModuleLastRefreshedAt | null,
): number | undefined {
    if (moduleLastRefreshedAt && BE_REFRESHABLE_MODULES.includes(moduleId)) {
        const timestamp = moduleLastRefreshedAt[moduleId];
        return typeof timestamp === 'number' && timestamp > 0 ? timestamp : undefined;
    }
    const moduleTasks = TAB_TO_TASK_TRACKER_MAP[moduleId] || [];
    const exclusiveTasks = moduleTasks.filter((step) => !CROSS_MODULE_TASK_STEPS.has(step));
    return latestCompleteDate(exclusiveTasks, taskTrackerMap) || latestCompleteDate(moduleTasks, taskTrackerMap);
}

export const isTabLockedForColumn = (
    tabId: string,
    tabLockStatus: Record<string, { isLocked?: boolean }> | null,
    taskTrackerMap: Record<string, { status?: string }> | null,
    secondaryModuleList: string[],
): boolean => {
    if (Array.isArray(secondaryModuleList) && !secondaryModuleList.includes(tabId)) {
        const polusEntry = tabLockStatus?.[tabId];
        if (polusEntry == null) return TAB_LOCK_IDS_AFTER_DIRECTORS_OVERVIEW.includes(tabId);
        return !!polusEntry.isLocked;
    }

    const taskTrackerHasEntries = !!taskTrackerMap && Object.keys(taskTrackerMap).length > 0;
    const anyStepCompleteOrFailed =
        taskTrackerHasEntries &&
        (TAB_TO_TASK_TRACKER_MAP[tabId] || []).some((step) => TAB_LOCK_COMPLETED_STATUSES.has(taskTrackerMap?.[step]?.status ?? ''));

    return !anyStepCompleteOrFailed || !!tabLockStatus?.[tabId]?.isLocked;
};
